import React from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { PreviewSection } from "../shared";
import { TextInput } from "@/components/ui/TextInput";
import { DateInput } from "@/components/ui/DateInput";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { ExpenseType } from "@/types/db/expenses";
import { ClientInput } from "@/components/inputs/ClientInput";
import { ProjectInput } from "@/components/inputs/ProjectInput";
import { ShiftInput } from "@/components/inputs/ShiftInput";
import { UserInput } from "@/components/inputs/UserInput";
import useAuth from "@/hooks/useAuth";

type ExpenseOverviewTabProps = {
  expense: ExpenseType | null;
  loading: boolean;
};

export function ExpenseOverviewTab({ expense, loading }: ExpenseOverviewTabProps) {
  const { editing, formData, updateFormData } = useEntityEdit<ExpenseType>();
  const { displayNames } = useAuth();

  // Unified data source
  const displayData = editing ? formData : expense;

  // ---------- Handlers ----------

  const handleAmountChange = (value: string) => {
    const amount = parseFloat(value) || 0;
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        amount,
      },
    });
  };

  const handleCategoryChange = (value: string) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        category: value || undefined,
      },
    });
  };

  const handleDateChange = (date: Date) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        date,
      },
    });
  };

  const handleClientChange = (clientId: string | null) => {
    updateFormData({
      ...formData,
      client_id: clientId || undefined,
      project_id: undefined, // Clear project when client changes
      meta_data: {
        ...formData.meta_data,
        shift_id: undefined, // Clear shift when client changes
      },
    });
  };

  const handleProjectChange = (projectId: string | null) => {
    updateFormData({
      ...formData,
      project_id: projectId || undefined,
      meta_data: {
        ...formData.meta_data,
        shift_id: undefined, // Clear shift when project changes
      },
    });
  };

  const handleShiftChange = (shiftId: string | null) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        shift_id: shiftId || undefined,
      },
    });
  };

  const handleUserChange = (userId: string | null) => {
    updateFormData({
      ...formData,
      meta_data: {
        ...formData.meta_data,
        user_id: userId || undefined,
      },
    });
  };

  const handleDescriptionChange = (text: string) => {
    updateFormData({
      ...formData,
      description: text,
    });
  };

  return (
    <View style={styles.container} pointerEvents={loading ? "none" : "auto"}>
      <ScrollView contentContainerStyle={{ gap: 8, padding: 16 }}>
        {/* Category */}
        <PreviewSection title="Category" titleStyle={styles.label}>
          <TextInput
            value={(displayData?.meta_data as any)?.category || ""}
            onChangeText={handleCategoryChange}
            editable={editing}
            loading={loading}
            placeholder="Category (optional)"
          />
        </PreviewSection>

        {/* Description */}
        <PreviewSection title="Description" titleStyle={styles.label}>
          <TextInput
            value={displayData?.description || ""}
            onChangeText={handleDescriptionChange}
            multiline
            numberOfLines={3}
            editable={editing}
            loading={loading}
            placeholder="Describe the expense..."
          />
        </PreviewSection>

        {/* Client */}
        <ClientInput
          value={displayData?.client_id || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={displayNames?.clients?.name || "Client"}
          placeholder={`Select ${displayNames?.clients?.name?.toLowerCase() || "client"}`}
          onChange={handleClientChange}
        />

        {/* Project */}
        <ProjectInput
          value={displayData?.project_id || null}
          clientId={displayData?.client_id}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={displayNames?.projects?.name || "Project"}
          placeholder={`Select ${displayNames?.projects?.name?.toLowerCase() || "project"}`}
          onChange={handleProjectChange}
        />

        {/* Shift */}
        <ShiftInput
          value={(displayData?.meta_data as any)?.shift_id || null}
          projectId={displayData?.project_id}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label={displayNames?.shifts?.name || "Shift"}
          placeholder={`Select ${displayNames?.shifts?.name?.toLowerCase() || "shift"} (optional)`}
          onChange={handleShiftChange}
        />

        {/* Amount */}
        <CurrencyInput
          testID="expense-amount-input"
          label="Amount"
          value={(displayData?.meta_data as any)?.amount || null}
          onChange={(v) => handleAmountChange(String(v ?? 0))}
          editable={editing}
          loading={loading}
        />

        {/* Date */}
        <DateInput
          label="Date"
          mode="date"
          value={(displayData?.meta_data as any)?.date}
          onChange={handleDateChange}
          editing={editing}
          loading={loading}
          placeholder="Select date"
          labelStyle={styles.label}
        />

        {/* User */}
        <UserInput
          value={(displayData?.meta_data as any)?.user_id || null}
          state={loading ? "loading" : editing ? "editing" : "idle"}
          label="Submitted By"
          placeholder="Select user"
          onChange={handleUserChange}
        />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  label: {
    fontSize: 11,
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
});
